import { createContext, useContext, useEffect, useState } from "react";
import { LOGIN_BASE_URL } from "../utils/constants";
import { useNavigate } from "react-router";
import { User } from "../models/user";
import { refreshAuthToken } from "../services/utils/tokenRefresh";
import { viewState } from "../store/viewStateStore";

interface AuthContextProps {
  login: (username, password) => void,
  logout: () => void,
  getProfile: () => User
}


const AuthContext: React.Context<AuthContextProps> = createContext({
  login: () => {},
  logout: () => {},
  getProfile: () => { return {} as User},
});

const AuthProvider = ({ children }) => {
  const navigate = useNavigate();

  const handleVisibilityChange = async () => {
    if (document.visibilityState === "visible" && getProfile()) {
      refresh()
    }
  }

  useEffect(() => {
    refresh();
    document.addEventListener("visibilitychange", handleVisibilityChange);
  }, [])

  const login = async (username: string, password: string) => {
    const response = await fetch(LOGIN_BASE_URL + "/auth/login", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({username, password}),
    });

    if (response.status === 200) {
      const res = await response.json();
      const profileResponse = await fetch(LOGIN_BASE_URL + "/auth/profile", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${res.accessToken}`,
        }
      });

      const profile = await profileResponse.json();

      // Session may have expired and another user logged in: don't inherit their list filters
      viewState.clearAll();
      localStorage.setItem("token", res.accessToken);
      localStorage.setItem("refreshToken", res.refreshToken);
      localStorage.setItem("profile", JSON.stringify(profile));

      setRefreshTimer()

      navigate("/dashboard");
    }
  }

  const logout = async () => {
    viewState.clearAll();
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("profile");

    navigate("/login");
  }

  const refresh = async () => {
    const refreshToken = localStorage.getItem("refreshToken");

    if (!refreshToken) {
      return
    }

    try {
      await refreshAuthToken();
      setRefreshTimer();
    } catch (error) {
      // Silently handle error - token refresh failed, but don't logout here
      // The axios interceptor will handle logout on API calls
    }
  }

  const setRefreshTimer = () => {
    setTimeout(refresh, 59 * 60 * 1000) // 59 minutes
  }

  const getProfile = () => {
    const profile = localStorage.getItem("profile");
    if (profile) {
      return JSON.parse(profile);
    }
  }

  return <AuthContext.Provider value={{login, logout, getProfile}}>{children}</AuthContext.Provider>;
};

export default AuthProvider;

export const useAuth = () => {
  return useContext(AuthContext);
};

export const getAuthUser = () => {
  const profile = localStorage.getItem("profile")
  if (profile) {
    return JSON.parse(profile);
  }
}
